import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';


import PageView from 'components/Common/PageView';

import Loading from 'xpi-black-ui/Base/Loading';

import { clientCanToBeSend } from 'utils/client';
import { SendProposal, ClearSentProposal } from 'redux-flow/actions/proposal';
import { LoadClients, FavoriteClient, UpdateClient, VerifyRisk, UploadClients, UpdateStateClient } from 'redux-flow/actions/clients';

import waConfig, { waDataReplace } from 'utils/waConfig';

import { convertNumberBR, convertToCurrency } from 'xpi-black-ui/Utils/helpers';
import useProposal from 'store/Proposals/state';
import { Props, DefaultProps } from './shape';
import SendProposalsSuccess from './SendProposalsSuccess';
import ClientsDialogWrapper from '../index';
import Clients from './Content';
import Header from './Header';
import Footer from './Footer';

export const typeBoletagemEnum = {
  amount: 1,
  quantity: 2,
};

const limitMaxNotionalBDR = 2000000;

const SendProposalClients = ({ assetName, operation, commission, onCloseDialog, isRubiYield, ...otherProps }) => {
  const dispatch = useDispatch();
  const {
    currentProposal,
    setCurrentProposal,
    isSendingPush,
    loading: isSendingProposal,
    proposal: hasProposal,
    clearSentProposal,
  } = useProposal();
  const { clients, loading } = useSelector(({ Clients }) => Clients);
  const { loading: sendingProposal, proposal, response, error, errors } = useSelector(({ Proposal }) => Proposal);

  const [clientsView, setClientsView] = useState(clients);
  const [totalCommission, setTotalCommission] = useState(0);
  const [loadingClients, setLoadingClients] = useState(false);

  const operationId = operation.codigoOperacao;
  const { disabledBasketClients, isBDR, precoReferencia, tamanhoLote, loteMinimo } = operation;

  const messageErrorBDR = `O valor nocional da operação está em {notional}. O valor nocional (Qtd * Preço Ref.) máx. para esta operação é de ${convertToCurrency(limitMaxNotionalBDR)}.`;
  const messageError = `Mín. ${convertNumberBR(isRubiYield ? loteMinimo.valor : loteMinimo, 2)} com incremental de ${convertNumberBR(tamanhoLote, 2)}`;

  const loadClientsFromExcel = (clients) => {
    if (clients?.length > 0) {
      dispatch(UploadClients({ clients, operationId }));
    }
  };

  const onSetClients = (updatedClients) => {
    const clients = updatedClients.map((client) => {
      let messageErrorQuantity = messageError;

      if (isBDR) {
        if (client.notional > limitMaxNotionalBDR) {
          hasError = true;
          messageErrorQuantity = messageErrorBDR.replace('{notional}', client.notionalView);
        }
      }

      let hasError = !isClientAvailable(client);
      return {
        ...client,
        hasError,
        messageError: messageErrorQuantity,
      };
    });

    setClientsView(clients);
    dispatch(UpdateStateClient({ clients }));
  };

  const isClientAvailable = (client) => {
    const clientsIsAvailable = clientCanToBeSend({
      client,
      minimum: isRubiYield ? loteMinimo.valor : loteMinimo,
      multipleBy: tamanhoLote,
    });

    if (isBDR) {
      return clientsIsAvailable && client?.notional < limitMaxNotionalBDR;
    }

    return clientsIsAvailable;
  };

  const calculateTotalCommission = (updatedClients) => {
    let comissionUpdated = 0;
    updatedClients.forEach((client) => {
      if (isClientAvailable(client)) {
        comissionUpdated += client.quantity * commission;
      }
    });
    setTotalCommission(comissionUpdated);
  };

  const updateClientState = (updatedClients) => {
    onSetClients(updatedClients);
    calculateTotalCommission(updatedClients);
  };

  const onCloseProposal = () => {
    updateClientState(clients);
    dispatch(ClearSentProposal());
    clearSentProposal();
  };

  useEffect(() => {
    dispatch(LoadClients({ operationId }));
    return () => {
      onCloseProposal();
    };
  }, []);

  useEffect(() => {
    setLoadingClients(false);
    setClientsView(clients);
  }, [clients]);

  const onSendConfirmProposal = () => {
    const selectedClients = clientsView.filter(client => client.isSelected);
    setCurrentProposal({ operationId, clients: selectedClients, isNewOperation: isRubiYield });
  };

  const onSendProposal = () => {
    const selectedClients = clientsView.filter(client => client.isSelected);
    dispatch(SendProposal({ operationId, clients: selectedClients, isNewOperation: isRubiYield }));
  };

  const onVerifyRiskClients = (clients) => {
    dispatch(VerifyRisk({ operationId, clients }));
  };

  const verifyHasClientValid = (updatedClients) => {
    const clientsSelected = updatedClients.filter(client => client.isSelected).length;
    const clientsCanToBeSend = updatedClients.filter(client => isClientAvailable(client));
    const canSendAllClients = clientsCanToBeSend.length === clientsSelected && clientsSelected > 0;

    return canSendAllClients;
  };

  const onSelectClient = (selectedClient) => {
    const updatedClients = clientsView.map(client => (
      client.clienteId === selectedClient.clienteId
        ? ({ ...client, isSelected: !client.isSelected })
        : client
    ));
    updateClientState(updatedClients);
  };

  const onReLoadClient = (clientId) => {
    setLoadingClients(true);
    dispatch(UpdateClient({ payload: clientId, operationId }));
  };

  const onSearch = (clientQuery) => {
    setLoadingClients(true);

    const clientsSelected = clientsView.filter(client => client.isSelected);
    dispatch(LoadClients({
      payload: clientQuery,
      clientsSelected,
      operationId,
    }));
  };

  const renderContent = () => (
    <>
      <PageView page={waDataReplace(waConfig.pageview.acquisition, `${assetName}/${operationId}`)} />
      <Header
        onSearch={onSearch}
        onCloseDialog={onCloseDialog}
      />
      <Clients
        clients={clientsView}
        hasError={error}
        errors={errors}
        isRubiYield={isRubiYield}
        loteMinimo={isRubiYield ? loteMinimo.valor : loteMinimo}
        loadingClients={loadingClients}
        onSelectClient={onSelectClient}
        onReLoadClient={onReLoadClient}
        precoReferencia={isRubiYield ? precoReferencia.valor : precoReferencia}
        sendingProposal={sendingProposal || isSendingProposal}
        onSendConfirmProposal={onSendConfirmProposal}
        updateClientState={updateClientState}
        favoriteClient={(client) => { dispatch(FavoriteClient(client)); }}
      />
      <Footer
        clients={clientsView}
        onSendProposal={onSendProposal}
        onSendConfirmProposal={onSendConfirmProposal}
        sendingProposal={sendingProposal || isSendingProposal}
        totalCommission={totalCommission}
        onVerifyRiskClients={onVerifyRiskClients}
        verifyHasClientValid={verifyHasClientValid}
        loadClientsFromExcel={loadClientsFromExcel}
        disabledBasketClients={disabledBasketClients}
        isRubiYield={isRubiYield}
      />
      <SendProposalsSuccess
        width="580px"
        height="auto"
        msgsError={response.mensagens}
        onClose={() => onCloseProposal()}
        open={(proposal && !sendingProposal) || (!isSendingProposal && hasProposal)}
      />
    </>
  );

  return (
    <ClientsDialogWrapper onCloseDialog={onCloseDialog} {...otherProps}>
      {loading && !loadingClients && <Loading fixed text="Carregando..." />}
      {renderContent()}
    </ClientsDialogWrapper>
  );
};

SendProposalClients.propTypes = Props;
SendProposalClients.defaultProps = DefaultProps;

export default SendProposalClients;
